import { useMutation, useQueryClient } from "@tanstack/react-query"
import { getSeamHttpClient } from "api-hooks/index"
import { deriveQueryKeyFromRequest } from "./use-seam-query"
import toast from "react-hot-toast"

export type UnlockLockDoorParams = {
  workspace_id: string
  device_id: string
}

export type UseUnlockLockOptions = {
  /**
   * When true, suppresses the bottom-right loading and success toasts so the
   * caller can render its own inline feedback. Error toasts are still shown.
   */
  silent?: boolean
}

export const useUnlockLock = ({
  silent = false,
}: UseUnlockLockOptions = {}) => {
  const qc = useQueryClient()
  return useMutation({
    onMutate: () => {
      const toast_id = silent
        ? undefined
        : toast.loading("Sending unlock request...")
      return { toast_id }
    },
    mutationFn: async (params: UnlockLockDoorParams) => {
      const client = getSeamHttpClient(params.workspace_id)
      return client.locks.unlockDoor({
        device_id: params.device_id,
      })
    },
    onSuccess: (res, params: UnlockLockDoorParams, context) => {
      const action_attempt = res
      const was_confirmed_by_device =
        action_attempt?.status === "success"
          ? (action_attempt as any)?.result?.was_confirmed_by_device
          : undefined
      if (!silent) {
        toast.success(
          was_confirmed_by_device === true
            ? "Device was confirmed as unlocked"
            : "Sent request to unlock the device",
          { id: context?.toast_id }
        )
      }
      qc.invalidateQueries({
        queryKey: ["devices", params.workspace_id, params.device_id],
      })
      qc.invalidateQueries({
        queryKey: deriveQueryKeyFromRequest(params.workspace_id, {
          url: {
            pathname: "/devices/get",
          },
          body: {
            device_id: params.device_id,
          },
        }),
      })
    },
    onError: (error: { message?: string }, _params, context) => {
      toast.error(error?.message ?? "Failed to unlock device", {
        id: context?.toast_id,
        position: "top-center",
      })
    },
  })
}
